import { authExchange, type AuthUtilities } from '@urql/exchange-auth'
import {
  Client,
  CombinedError,
  createClient,
  fetchExchange,
  subscriptionExchange,
  type AnyVariables,
  type Exchange,
  type Operation,
  type OperationResult,
  type SubscriptionForwarder
} from '@urql/core'
import { onEnd, pipe, subscribe, type Subscription } from 'wonka'
import { defineNuxtPlugin, type NuxtApp } from 'nuxt/app'
import { retryExchange } from '@urql/exchange-retry'
import { createClient as createWSClient } from 'graphql-ws'
import type { AuthTokenHelper } from '~/composables/useAuthToken'
import {
  DEFAULT_SUB_BASE_DELAY_MS,
  DEFAULT_SUB_MAX_DELAY_MS,
  DEFAULT_SUB_MAX_RETRIES,
  nextSubscriptionRetryDelayMs,
  type GqlSubOptions,
  type GqlSubscriptionHandle
} from '~/utils/graphqlRetry'

// Result data typed as `any`: queries are raw strings, not typed documents, so shape is arbitrary per call
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GqlResult = OperationResult<any>

// Resolves the Hasura GraphQL endpoint for a data domain. `scheme` is 'https' for
// queries/mutations and 'wss' for subscriptions; every other part of the URL is
// identical, which is why query/mutate/sub previously each carried a copy of this block.
function buildDataEndpoint (scheme: 'https' | 'wss', dataDomain?: string): string {
  const envVars = useRuntimeConfig()

  if (dataDomain === 'main') {
    return `${scheme}://${envVars.public.DATA_MAIN_DOMAIN}/v1/graphql`
  }

  if (dataDomain) {
    return `${scheme}://${dataDomain}.${envVars.public.DATA_DOMAIN}/v1/graphql`
  }

  const { currentClient } = useCurrentClient()

  // Guard against a request to `https://.sec-auditor.com/...` when the current
  // client has not been initialized yet (empty client_data_endpoint).
  if (!currentClient.value.client_data_endpoint) {
    throw new Error('GraphQL request attempted before current client was initialized (empty client_data_endpoint)')
  }

  return `${scheme}://${currentClient.value.client_data_endpoint}.${envVars.public.DATA_DOMAIN}/v1/graphql`
}

export default defineNuxtPlugin(() => {
  const tokenHelper = useAuthToken()
  const clientCache = new Map<string, Client>()

  function getClient (dataEndpoint: string): Client {
    if (clientCache.has(dataEndpoint)) {
      return clientCache.get(dataEndpoint)!
    }
    const client = createClient({
      url: dataEndpoint,
      exchanges: getExchanges(tokenHelper),
      preferGetMethod: false
    })
    clientCache.set(dataEndpoint, client)
    return client
  }

  // Shared execution path for one-shot query/mutation operations. Both resolve the
  // data endpoint the same way and share the same best-effort error handling; the
  // only difference is which urql client method gets called.
  const runOperation = async (
    kind: 'query' | 'mutation',
    q: string,
    variables: AnyVariables,
    dataDomain?: string
  ): Promise<GqlResult | undefined> => {
    const nuxtApp: NuxtApp = useNuxtApp()

    try {
      if (!dataDomain) {
        await waitForClientDataEndpoint()
      }

      const dataEndpoint = buildDataEndpoint('https', dataDomain)

      let client: Client
      try {
        client = await getClient(dataEndpoint)
      } catch (e) {
        nuxtApp.$logger.error(e)
      }

      return kind === 'query'
        ? await client.query(q, variables)
        : await client.mutation(q, variables).toPromise()
    } catch (e) {
      nuxtApp.$logger.error(e)
      nuxtApp.$logger.info(q)
      await new Promise(resolve => setTimeout(resolve, 1000)) // wait before retrying
    }
  }

  // exported graphql query function
  const query = (q: string, variables: AnyVariables = {}, dataDomain?: string): Promise<GqlResult | undefined> =>
    runOperation('query', q, variables, dataDomain)

  // exported graphql subscription function
  // Resubscribes with exponential backoff whenever the subscription errors or ends
  // unexpectedly, so every caller gets bounded retry for free without reimplementing
  // it. The returned handle stays valid across retries; unsubscribe() stops both the
  // active subscription and any pending retry.
  const sub = async (
    q: string,
    variables: AnyVariables = {},
    onNext: (data: GqlResult) => void,
    options: GqlSubOptions = {}
  ): Promise<GqlSubscriptionHandle> => {
    const nuxtApp: NuxtApp = useNuxtApp()

    const {
      dataDomain,
      maxRetries = DEFAULT_SUB_MAX_RETRIES,
      baseDelayMs = DEFAULT_SUB_BASE_DELAY_MS,
      maxDelayMs = DEFAULT_SUB_MAX_DELAY_MS,
      onRetriesExhausted
    } = options

    let wonkaSub: Subscription | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0
    let stopped = false

    const dropCurrent = (): void => {
      const current = wonkaSub
      wonkaSub = null
      current?.unsubscribe()
    }

    const teardown = (): void => {
      stopped = true
      if (retryTimer) {
        clearTimeout(retryTimer)
        retryTimer = null
      }
      dropCurrent()
    }

    const scheduleRetry = (): void => {
      if (stopped || retryTimer) {
        return
      }

      dropCurrent()

      if (attempt >= maxRetries) {
        stopped = true
        nuxtApp.$logger.error(`GraphQL subscription failed permanently after ${attempt} retries`)
        onRetriesExhausted?.()
        return
      }

      attempt++
      retryTimer = setTimeout(() => {
        retryTimer = null
        connect().catch(e => nuxtApp.$logger.error(e))
      }, nextSubscriptionRetryDelayMs(attempt, baseDelayMs, maxDelayMs))
    }

    async function connect (): Promise<void> {
      if (stopped) {
        return
      }

      let client: Client
      try {
        if (!dataDomain) {
          await waitForClientDataEndpoint()
        }

        client = await getClient(buildDataEndpoint('wss', dataDomain))
      } catch (e) {
        nuxtApp.$logger.error(e)
        scheduleRetry()
        return
      }

      // guards this attempt against handing off to scheduleRetry more than once
      // (onEnd fires on complete, error-teardown AND our own unsubscribe)
      let settled = false
      const failThisAttempt = (message: string): void => {
        if (stopped || settled) {
          return
        }
        settled = true
        nuxtApp.$logger.warn(message)
        scheduleRetry()
      }

      try {
        const thisSub = pipe(
          client.subscription(q, variables),
          onEnd(() => failThisAttempt('GraphQL subscription ended unexpectedly, retrying')),
          subscribe((result) => {
            if (stopped || settled) {
              return
            }
            if (result.error) {
              failThisAttempt(`GraphQL subscription returned an error: ${result.error.message || result.error}`)
              return
            }
            // healthy emission - reset the backoff
            attempt = 0
            onNext(result)
          })
        )

        // caller unsubscribed, or the subscription already ended, while it was
        // still being wired up
        if (stopped || settled) {
          thisSub.unsubscribe()
        } else {
          wonkaSub = thisSub
        }
      } catch (e) {
        nuxtApp.$logger.error(e)
        failThisAttempt(`Could not start GraphQL subscription: ${e}`)
      }
    }

    await connect()

    return { unsubscribe: teardown }
  }

  const mutate = (q: string, variables: AnyVariables = {}, dataDomain?: string): Promise<GqlResult | undefined> =>
    runOperation('mutation', q, variables, dataDomain)

  const gql = { query, sub, mutate }

  return {
    provide: {
      gql
    }
  }
})

function getExchanges (tokenHelper: AuthTokenHelper): Exchange[] {
  const envVars = useRuntimeConfig()
  const nuxtApp: NuxtApp = useNuxtApp()
  const { currentClient } = useCurrentClient()

  const dataEndpoint = currentClient.value.client_data_endpoint
  let isWsSupported = false
  let wsErrorToastShown = false

  // shared sign-out path for genuinely dead sessions (refresh token expired/revoked),
  // deduplicated across all urql clients (one per data endpoint)
  const handleDeadSession = (): void => {
    tokenHelper.handleDeadSession()
  }

  const retryExchangeOptions = {
    initialDelayMs: 1000,
    maxDelayMs: 15000,
    randomDelay: true,
    maxNumberAttempts: 3
  }

  const auth = authExchange(async (utilities: AuthUtilities) => {
    await Promise.resolve() // authExchange requires the initializer to return a Promise<AuthConfig>
    return {
      async refreshAuth (): Promise<void> {
        const freshSession = await tokenHelper.getFreshSession()
        if (!freshSession) {
          handleDeadSession()
        }
      },
      didAuthError (error: CombinedError, _operation: Operation): boolean | undefined {
        if (error?.networkError) {
          nuxtApp.$logger.info('network error')
          nuxtApp.$logger.info(error.networkError)
          nuxtApp.$logger.info(`is auth error? ${error.networkError?.reason?.includes('JWTExpired')}`)
          return error.networkError?.reason?.includes('JWTExpired')
        }

        if (error?.graphQLErrors) {
          nuxtApp.$logger.info('graphql error')
          nuxtApp.$logger.info(error.graphQLErrors)
          nuxtApp.$logger.info(error.graphQLErrors?.[0])

          if (tokenHelper.isTokenStale(0) || error.graphQLErrors?.[0].message === 'Could not verify JWT: JWTExpired') {
            handleDeadSession()
          }

          return false
        }
      },
      // cheap local expiry check against the live session, no network call
      willAuthError (_operation: Operation): boolean {
        return tokenHelper.isTokenStale()
      },
      addAuthToOperation (operation: Operation): Operation {
        const accessToken = tokenHelper.session.value?.accessToken
        return accessToken
          ? utilities.appendHeaders(operation, {
            Authorization: `Bearer ${accessToken}`,
            'x-hasura-role': tokenHelper.getRole()
          })
          : operation
      }
    }
  })

  const exchanges = [auth, retryExchange(retryExchangeOptions), fetchExchange]

  if (process.client) {
    const subscriptionClient = createWSClient({
      url: dataEndpoint ? 'wss://' + dataEndpoint + '.' + envVars.public.DATA_DOMAIN + '/v1/graphql' : 'wss://' + envVars.public.DATA_MAIN_DOMAIN + '/v1/graphql',
      lazy: true,
      retryAttempts: 3,
      connectionParams: async () => {
        const freshSession = await tokenHelper.getFreshSession()
        return {
          headers: {
            Authorization: `Bearer ${freshSession?.accessToken ?? ''}`,
            'x-hasura-role': tokenHelper.getRole() || ''
          }
        }
      },
      on: {
        connected: () => {
          isWsSupported = true
          nuxtApp.$logger.info('ws connected')
        },
        closed: () => {
          if (!isWsSupported && !wsErrorToastShown) {
            // we were never connected, so probably not supported
            nuxtApp.$toast.error('Fehler beim Verbindungsaufbau', 'Konnte kein Websocket öffnen, vermutlich sind diese nicht unterstützt oder wurden blockiert')
            wsErrorToastShown = true // only show once
          }

          nuxtApp.$logger.info('ws closed') // connection rejected, probably not supported
        },
        disconnected: () => {
          nuxtApp.$logger.info('ws discconnected')
        },
        // eslint-disable-next-line require-await
        error: async (error) => {
          nuxtApp.$logger.error(error)
        },
        reconnected: () => {
          nuxtApp.$logger.info('ws reconnected')
        },
        reconnecting: () => {
          nuxtApp.$logger.info('ws reconnecting')
        }
      }
    })

    const forwardSubscription: SubscriptionForwarder = (request) => {
      const input = { ...request, query: request.query || '' }
      return {
        subscribe (sink): { unsubscribe: () => void } {
          const unsubscribe = subscriptionClient.subscribe(input, sink)
          return { unsubscribe }
        }
      }
    }

    exchanges.push(
      subscriptionExchange({
        forwardSubscription
      })
    )
  }
  return exchanges
}
